import {
  MOUSE,
  Vector2,
  Vector3,
  Vector4,
  Quaternion,
  Matrix4,
  Spherical,
  Box3,
  Sphere,
  Raycaster,
  MathUtils,
  PerspectiveCamera,
  Object3D,
  Group,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
} from "three";
import CameraControls from "camera-controls";

import { Context } from "../context";

import { DefensiveActionControllerStrategy } from "./auto/action";
import { LandmarkActionControllerStrategy } from "./auto/landmark";
import { PersonalControllerStrategy } from "./auto/personal";
import { CameraModeOptions, ControllerStrategy } from "./strategy/strategy";
import {
  BallTrackerControllerStrategy,
  LookAtBallControllerStrategy,
  LookAtControllerStrategy,
  PairControllerStrategy,
  SpinControllerStrategy,
  ThirdPersonControllerStrategy,
} from "./strategy/tracking";
import {
  ZoomDollyControllerStrategy,
  ZoomFOVControllerStrategy,
} from "./strategy/zoom";
import { MapControllerStrategy } from "./strategy/general/map";
import {
  MinHeightControllerStrategy,
  StadiumCollisionControllerStrategy,
} from "./strategy/collision";
import { StrikezoneControllerStrategy } from "./auto/strikezone";
import { PitcherControlStrategy } from "./auto/pitcher";
import { BatterControlStrategy } from "./auto/batter";
import { PositionIdFollowStrategy } from "./strategy/tracking/position-id";
import { InitControllerStrategy } from "./auto/init";
import { JumbotronControllerStrategy } from "./auto/jumbotron";

const threeSubset = {
  MOUSE: MOUSE,
  Vector2: Vector2,
  Vector3: Vector3,
  Vector4: Vector4,
  Quaternion: Quaternion,
  Matrix4: Matrix4,
  Spherical: Spherical,
  Box3: Box3,
  Sphere: Sphere,
  Raycaster: Raycaster,
  MathUtils: {
    DEG2RAD: MathUtils.DEG2RAD,
    clamp: MathUtils.clamp,
  },
};

CameraControls.install({ THREE: threeSubset });

export enum CameraMode {
  INIT = "init",
  MAP = "map",
  ORBIT = "orbit",
  FREE = "free",
  SPIN = "spin",
  PAIR = "pair",
  PAIR_SIMPLE = "pair-simple",
  LOOK_AT_PLAYER = "lookat-player",
  LOOK_AT_BALL = "lookat-ball",
  LOOK_AT_SIMPLE = "lookat-simple",
  THIRD = "3rd-person",
  BALL = "ball",
  ACTION = "action",
  LANDMARK = "landmark",
  PERSONAL = "personal",
  STRIKEZONE = "strikezone",
  JUMBOTRON = "jumbotron",
  BATTER = "batter",
  PITCHER = "pitcher",
}

export enum CameraLandmark {
  HH = "HH",
  PITCHER_POV_L = "PITCHER_POV_L",
  PITCHER_POV_R = "PITCHER_POV_R",
  PITCHER_PLATE_MIDPOINT = "PITCHER_PLATE_MIDPOINT",
  MOUND_CENTER = "MOUND_CENTER",
  MOUND_LEFT = "MOUND_LEFT",
  MOUND_RIGHT = "MOUND_RIGHT",
  AUTO_POS_1 = "AUTO_POS_1",
  AUTO_POS_2 = "AUTO_POS_2",
  AUTO_POS_3 = "AUTO_POS_3",
  AUTO_POS_4 = "AUTO_POS_4",
  AUTO_POS_5 = "AUTO_POS_5",
  AUTO_POS_6 = "AUTO_POS_6",
  UMP_LEFT = "UMP_LEFT",
  UMP_RIGHT = "UMP_RIGHT",
  BATTER_TARGET_L = "BATTER_TARGET_L",
  BATTER_TARGET_R = "BATTER_TARGET_R",
  STRIKEZONE_CENTER = "STRIKEZONE_CENTER",
  STRIKEZONE_CENTER_MOBILE = "STRIKEZONE_CENTER_MOBILE",
  STRIKEZONE_CENTER_BACK = "STRIKEZONE_CENTER_BACK",

  INIT_POS = "INIT_POS",
  INIT_TARGET = "INIT_TARGET",
}

const autoPosHeight = 10;

export const CameraLandmarkLookup: {
  [key in CameraLandmark]: Vector3;
} = {
  HH: new Vector3(0, 25, 50),
  PITCHER_POV_L: new Vector3(2, 5, -54),
  PITCHER_POV_R: new Vector3(-2, 5, -54),
  PITCHER_PLATE_MIDPOINT: new Vector3(0, 1.5, -30),
  MOUND_CENTER: new Vector3(0, 7, -105),
  MOUND_LEFT: new Vector3(4, 7, -105),
  MOUND_RIGHT: new Vector3(-4, 7, -105),
  AUTO_POS_1: new Vector3(95, autoPosHeight, -65),
  AUTO_POS_2: new Vector3(0, autoPosHeight, -80),
  AUTO_POS_3: new Vector3(-95, autoPosHeight, -65),
  AUTO_POS_4: new Vector3(174, autoPosHeight, -154),
  AUTO_POS_5: new Vector3(-174, autoPosHeight, -154),
  AUTO_POS_6: new Vector3(0, autoPosHeight, -160),
  UMP_LEFT: new Vector3(-4.3, 7.5, 17.6),
  UMP_RIGHT: new Vector3(2.1, 6.2, 14),
  BATTER_TARGET_L: new Vector3(-1, 3.6, 4.9),
  BATTER_TARGET_R: new Vector3(1, 3.6, 4.9),
  STRIKEZONE_CENTER: new Vector3(0, 3.75, 13.0),
  STRIKEZONE_CENTER_MOBILE: new Vector3(0, 4.5, 40.0),
  STRIKEZONE_CENTER_BACK: new Vector3(0, 4.5, 45.0),

  INIT_POS: new Vector3(1, 500, -105),
  INIT_TARGET: new Vector3(0, 3, -105),
};

export function nearestLandmark(
  position: Vector3,
  blackList = [CameraLandmark.MOUND_LEFT, CameraLandmark.MOUND_RIGHT]
): CameraLandmark {
  let bestDist = Infinity;
  let bestFit: CameraLandmark = CameraLandmark.HH;

  for (const item of Object.values(CameraLandmark)) {
    if (!blackList.includes(item)) {
      const dist = CameraLandmarkLookup[item].distanceToSquared(position);
      if (dist < bestDist) {
        bestFit = item;
        bestDist = dist;
      }
    }
  }

  return bestFit;
}

export enum SmoothTimes {
  VERY_SLOW = 3,
  SLOW = 2,
  MEDIUM_SLOW = 1,
  MEDIUM = 0.5,
  FAST = 0.25,
  VERY_FAST = 0.1,
  INSTANT = 0,
  DEFAULT = FAST,
}

type CameraState = {
  positionId: number;
  positionIdOther: number;
  tracked?: Object3D;
  trackedOther?: Object3D;
  homePos?: Vector3;
  teleport: boolean;
  autoTrack: boolean;
  following: boolean;

  // camera position
  camDist: number;

  zoomConfig: {
    dolly: {
      min: number;
      max: number;
      speed: number;
      speedTouch: number;
    };
    fov: {
      min: number;
      max: number;
      speed: number;
    };
  };
};

/**
 * Wrapper class for main strategy pattern (https://en.wikipedia.org/wiki/Strategy_pattern)
 * state machine for cameras.
 *
 * Owns two lists of strategies, which it manages the lifecycle of (init / teardown)
 * as well as the update. Order of list is important and the later entries will
 * take precedent if there is conflicting behaviour in the strategies.
 *
 * The two strategy lists allow for:
 * - base camera modes - the main `strategies` list - which is only settable by chosing from
 *                       a set of fixed strategy lists - called "modes".
 * - modifiers (auxStrategies) - allow the client code to "manually" add to a list of strategies
 *                               modifying the base camera mode behaviour.
 */
export class CameraController {
  // TODO: work out how to do friend classes in ts - so this can be private
  camera: PerspectiveCamera;
  controls?: CameraControls;

  objects: Group = new Group();

  targetSphere = {
    mesh: new Mesh(
      new SphereGeometry(2, 10),
      new MeshBasicMaterial({ color: 0xff0000 })
    ),
  };

  strategies: ControllerStrategy[] = [];
  auxStrategies: Map<typeof ControllerStrategy, ControllerStrategy> = new Map();

  // TODO: this other stuff is horrible - think of a nice way to encapsulate things
  state: CameraState = {
    positionId: 10,
    positionIdOther: 1,
    teleport: false,
    autoTrack: true,
    following: true,
    camDist: 30,
    zoomConfig: {
      dolly: {
        min: 5,
        max: 600,
        speed: 0.15,
        speedTouch: 1,
      },
      fov: {
        min: 0.5,
        max: 10,
        speed: 0.5,
      },
    },
  };

  contextRef: Context;

  domRef: HTMLCanvasElement;

  private _mode: { mode: CameraMode; fromManager: boolean } = {
    mode: CameraMode.MAP,
    fromManager: false,
  };

  private target = new Vector3();

  private arrivedCb?: (pos: Vector3) => boolean;

  constructor(context: Context, auxs?: typeof ControllerStrategy[]) {
    const { camera, renderer, state } = context;

    this.domRef = renderer.domElement;
    this.camera = camera;
    this.controls = new CameraControls(camera, this.domRef);
    this.controls.setLookAt(
      CameraLandmarkLookup[CameraLandmark.INIT_POS].x,
      CameraLandmarkLookup[CameraLandmark.INIT_POS].y,
      CameraLandmarkLookup[CameraLandmark.INIT_POS].z,
      CameraLandmarkLookup[CameraLandmark.INIT_TARGET].x,
      CameraLandmarkLookup[CameraLandmark.INIT_TARGET].y,
      CameraLandmarkLookup[CameraLandmark.INIT_TARGET].z
    );

    this.controls.smoothTime = SmoothTimes.VERY_SLOW;

    this.contextRef = context;

    this.targetSphere.mesh.visible = false;

    this.objects.add(this.targetSphere.mesh);
    context.scene.add(this.objects);

    this.setMode(state.cameraMode);
    if (auxs) {
      for (const ctor of auxs) {
        this.addAuxStrategy(ctor);
      }
    }
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    for (const strategy of [
      ...this.strategies,
      ...this.auxStrategies.values(),
    ]) {
      strategy.update(deltaS, gameTimeDelta, frameNum);
    }
    if (!this.controls) return;

    this.controls.update(deltaS);

    this.controls.getTarget(this.target);

    if (this.arrivedCb && this.arrivedCb(this.controls.camera.position)) {
      this.arrivedCb = undefined;
    }
  }

  setMode(mode: CameraMode, options?: CameraModeOptions) {
    const strategiesToRemove = this.strategies.splice(
      options?.fromManager ? 1 : 0
    );

    for (const strategy of strategiesToRemove) {
      strategy.teardown();
    }

    const modeStrats = cameraModeMap[mode];
    for (const ctor of modeStrats) {
      this.register(ctor, false, options);
    }

    if (!this._mode.fromManager || options?.fromManager) {
      this._mode = { mode, fromManager: options?.fromManager || false };
    }

    this.contextRef.callbacks.onCameraChange({ mode, options });
  }

  addAuxStrategy(ctor: typeof ControllerStrategy) {
    if (this.auxStrategies.has(ctor)) {
      console.warn("re-registering existing aux cam strategy", ctor.name);
      return;
    }
    this.register(ctor, true);
  }

  removeAuxStrategy(ctor: typeof ControllerStrategy) {
    const strategy = this.auxStrategies.get(ctor);
    if (!strategy) return;

    strategy.teardown();
    this.auxStrategies.delete(ctor);
  }

  clearAuxStrategies() {
    for (const strategy of this.auxStrategies.values()) {
      strategy.teardown();
    }

    this.auxStrategies.clear();
  }

  get mode() {
    return this._mode.mode;
  }

  setLocation(
    landmark: CameraLandmark,
    arrivedCb?: (pos: Vector3) => boolean,
    enableTransition = true
  ) {
    if (
      this.mode === CameraMode.MAP ||
      this.mode === CameraMode.ORBIT ||
      this.mode === CameraMode.LANDMARK
    ) {
      const position = CameraLandmarkLookup[landmark];
      if (this.controls) {
        this.controls.getTarget(this.target);

        this.controls.setLookAt(
          position.x,
          position.y,
          position.z,
          0,
          this.target.y,
          -40,
          enableTransition
        );
      } else {
        // TODO: use tweening here
        console.error("no camera controller in setLocation");
        this.camera.position.copy(position);
        this.camera.updateProjectionMatrix();
      }

      if (arrivedCb) {
        this.arrivedCb = arrivedCb;
      }
    }
  }

  set enabled(enabled: boolean) {
    if (this.controls) {
      this.controls.enabled = enabled;
    }
  }

  register(
    ctor: typeof ControllerStrategy,
    aux = false,
    options?: CameraModeOptions
  ) {
    const strategy = new ctor();
    strategy.registered = this;
    strategy.init(this.contextRef, options);

    if (aux) {
      this.auxStrategies.set(ctor, strategy);
    } else {
      this.strategies.push(strategy);
    }
  }

  teardown() {
    this.clearAuxStrategies();
    for (const strategy of this.strategies) {
      strategy.teardown();
    }
  }
}

// this map is what defines the behaviour of each CameraMode
export const cameraModeMap: {
  [key in CameraMode]: typeof ControllerStrategy[];
} = {
  [CameraMode.INIT]: [InitControllerStrategy],
  [CameraMode.MAP]: [
    ZoomDollyControllerStrategy,
    MinHeightControllerStrategy,
    StadiumCollisionControllerStrategy,
    MapControllerStrategy,
  ],
  [CameraMode.ORBIT]: [
    ZoomFOVControllerStrategy,
    MinHeightControllerStrategy,
    StadiumCollisionControllerStrategy,
  ],
  [CameraMode.FREE]: [
    ZoomDollyControllerStrategy,
    StadiumCollisionControllerStrategy,
  ],
  [CameraMode.SPIN]: [
    ZoomDollyControllerStrategy,
    PositionIdFollowStrategy,
    SpinControllerStrategy,
    StadiumCollisionControllerStrategy,
  ],
  [CameraMode.PAIR]: [
    ZoomDollyControllerStrategy,
    PairControllerStrategy,
    PositionIdFollowStrategy,
    StadiumCollisionControllerStrategy,
  ],
  [CameraMode.PAIR_SIMPLE]: [
    ZoomDollyControllerStrategy,
    PairControllerStrategy,
    StadiumCollisionControllerStrategy,
  ],
  [CameraMode.LOOK_AT_PLAYER]: [
    ZoomFOVControllerStrategy,
    PositionIdFollowStrategy,
    LookAtControllerStrategy,
  ],
  [CameraMode.LOOK_AT_BALL]: [
    ZoomFOVControllerStrategy,
    LookAtBallControllerStrategy,
  ],
  [CameraMode.LOOK_AT_SIMPLE]: [
    ZoomFOVControllerStrategy,
    LookAtControllerStrategy,
  ],
  [CameraMode.THIRD]: [ThirdPersonControllerStrategy],
  [CameraMode.BALL]: [
    ZoomDollyControllerStrategy,
    BallTrackerControllerStrategy,
    StadiumCollisionControllerStrategy,
  ],
  [CameraMode.LANDMARK]: [LandmarkActionControllerStrategy],
  [CameraMode.ACTION]: [DefensiveActionControllerStrategy],
  [CameraMode.PERSONAL]: [PersonalControllerStrategy],
  [CameraMode.STRIKEZONE]: [StrikezoneControllerStrategy],
  [CameraMode.JUMBOTRON]: [JumbotronControllerStrategy],
  [CameraMode.BATTER]: [BatterControlStrategy],
  [CameraMode.PITCHER]: [PitcherControlStrategy],
};
